import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { GET_ME } from "graphql/query/user-gql";
import { GET_EVENT_SUBCONTRACTORS } from "graphql/query/event-subcontractor-gql";
import {
    CREATE_EVENT_SUBCONTRACTOR,
    UPDATE_EVENT_SUBCONTRACTOR,
    DELETE_EVENT_SUBCONTRACTOR,
} from "graphql/mutation/event-subcontractor-gql";
import {
    assertBookingResult,
    bookingError,
    canManageEventBookings,
    isBookedWorkersValid,
    normalizeBookingError,
    readEventBookings,
} from "../helpers/booking-policy";

const useBookedSubcontractors = ({ eventID }) => {
    const client = useApolloClient();
    const { data, loading: meLoading, error: meError } = useQuery(GET_ME, {
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });
    const me = data?.me;
    const canEdit = !meLoading && !meError && Boolean(me?.id) && canManageEventBookings(me);
    const canRead = Boolean(eventID && me?.id && !meLoading && !meError);
    const contextKey = JSON.stringify([
        String(eventID || ""), String(me?.id || ""), String(me?.role?.id || ""),
        meLoading ? "loading" : meError ? "error" : canEdit ? "edit" : "view",
    ]);
    const context = useRef({ key: contextKey });
    if (context.current.key !== contextKey) context.current = { key: contextKey };
    const activeContext = context.current;
    const mounted = useRef(false);
    const requestNumber = useRef(0);
    const pending = useRef(null);
    const [ saving, setSaving ] = useState(false);
    const [ state, setState ] = useState({ context: null, rows: [], loading: false, error: null });
    const [ warning, setWarning ] = useState(null);
    const latest = useRef();
    latest.current = { context: activeContext, canRead, canEdit, state, eventID: String(eventID || "") };

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const isCurrent = useCallback((requestContext) => (
        mounted.current && latest.current.context === requestContext
    ), []);

    const load = useCallback(async (requestContext, verify) => {
        const current = latest.current;
        if (!isCurrent(requestContext)) throw bookingError("STALE_CONTEXT");
        if (!current.canRead) throw bookingError("FORBIDDEN");
        const sequence = ++requestNumber.current;
        setState({ context: requestContext, rows: [], loading: true, error: null });
        try
        {
            const result = await client.query({
                query: GET_EVENT_SUBCONTRACTORS,
                variables: { ee_id: current.eventID },
                fetchPolicy: "no-cache",
                context: { queryDeduplication: false },
            });
            if (!isCurrent(requestContext) || sequence !== requestNumber.current)
                throw bookingError("STALE_CONTEXT");
            const rows = readEventBookings(result, current.eventID);
            if (verify && !verify(rows)) throw bookingError("UNCONFIRMED_REFRESH");
            setState({ context: requestContext, rows, loading: false, error: null });
            setWarning(null);
            return rows;
        }
        catch (error)
        {
            const failure = normalizeBookingError(error);
            if (isCurrent(requestContext) && sequence === requestNumber.current)
                setState({ context: requestContext, rows: [], loading: false, error: failure });
            throw failure;
        }
    }, [ client, isCurrent ]);

    const refresh = useCallback(() => load(activeContext), [ load, activeContext ]);

    useEffect(() => {
        setWarning(null);
        if (canRead) load(activeContext).catch(() => {});
        return () => { requestNumber.current += 1; };
    }, [ canRead, activeContext, load ]);

    const write = useCallback(async (values, booking, deleting = false) => {
        const current = latest.current;
        if (!isCurrent(activeContext)) throw bookingError("STALE_CONTEXT");
        if (!current.canEdit) throw bookingError("FORBIDDEN");
        if (pending.current) throw bookingError("REQUEST_PENDING");
        if (current.state.context !== activeContext || current.state.loading || current.state.error)
            throw bookingError("NEEDS_REFRESH");
        const rows = current.state.rows;
        const existing = booking && rows.find((row) => String(row.id) === String(booking.id));
        if (booking && (!existing || String(booking.ee_id) !== current.eventID ||
            String(booking.subcontractor_id) !== String(existing.subcontractor_id)))
            throw bookingError("STALE_BOOKING");
        if (deleting && !existing) throw bookingError("STALE_BOOKING");

        const companyID = existing ? String(existing.subcontractor_id) : String(values?.subcontractor_id || "");
        if (!deleting)
        {
            if (!isBookedWorkersValid(values?.booked_workers)) throw bookingError("INVALID_QUANTITY");
            if (!companyID.trim()) throw bookingError("COMPANY_REQUIRED");
            if (!existing && rows.some((row) => String(row.subcontractor_id) === companyID))
                throw bookingError("DUPLICATE_COMPANY");
        }
        const input = {
            ee_id: current.eventID,
            subcontractor_id: companyID,
            booked_workers: values?.booked_workers,
            ...(existing ? { id: String(existing.id) } : {}),
        };
        const mutation = deleting ? DELETE_EVENT_SUBCONTRACTOR :
            existing ? UPDATE_EVENT_SUBCONTRACTOR : CREATE_EVENT_SUBCONTRACTOR;
        const field = deleting ? "eeSubcontractorDelete" :
            existing ? "eeSubcontractorUpdate" : "eeSubcontractorCreate";
        const operation = {};
        pending.current = operation;
        setSaving(true);
        setWarning(null);
        try
        {
            let saved;
            try
            {
                const result = await client.mutate({
                    mutation,
                    variables: deleting ? { id: String(existing.id) } : { input },
                    fetchPolicy: "no-cache",
                });
                if (!isCurrent(activeContext)) return false;
                assertBookingResult(result);
                const payload = result?.data?.[ field ];
                saved = payload?.exhibitionEventSubcontractor;
                if (!payload || (!deleting && (!saved?.id ||
                    String(saved.ee_id) !== current.eventID ||
                    String(saved.subcontractor_id) !== companyID ||
                    saved.booked_workers !== input.booked_workers ||
                    (existing && String(saved.id) !== String(existing.id)))))
                    throw bookingError("UNCONFIRMED_MUTATION", payload?.message || "UNCONFIRMED_MUTATION");
            }
            catch (error)
            {
                if (!isCurrent(activeContext)) return false;
                // Reconcile an uncertain response or a concurrent booking before another submission.
                await load(activeContext).catch(() => {});
                if (!isCurrent(activeContext)) return false;
                throw normalizeBookingError(error);
            }
            if (deleting)
            {
                let freshRows;
                try
                {
                    freshRows = await load(activeContext);
                }
                catch (error)
                {
                    if (!isCurrent(activeContext)) return false;
                    const failure = bookingError("DELETE_UNCONFIRMED", error.message);
                    setWarning({ context: activeContext, error: failure });
                    throw failure;
                }
                // MessagePayload alone has no machine-readable success flag.
                if (freshRows.some((row) => String(row.id) === String(existing.id)))
                    throw bookingError("DELETE_UNCONFIRMED");
                return isCurrent(activeContext);
            }
            try
            {
                await load(activeContext, (freshRows) =>
                    freshRows.some((row) => String(row.id) === String(saved.id) &&
                        row.booked_workers === input.booked_workers &&
                        String(row.subcontractor_id) === companyID));
            }
            catch (error)
            {
                if (isCurrent(activeContext)) setWarning({ context: activeContext, error });
            }
            return isCurrent(activeContext);
        }
        finally
        {
            if (pending.current === operation)
            {
                pending.current = null;
                if (mounted.current) setSaving(false);
            }
        }
    }, [ activeContext, client, isCurrent, load ]);

    const saveBooking = useCallback((values, booking) => write(values, booking), [ write ]);
    const deleteBooking = useCallback((booking) => write(null, booking, true), [ write ]);
    const currentState = state.context === activeContext ? state : null;

    return {
        contextKey,
        rows: currentState?.rows || [],
        loading: meLoading || (canRead && (!currentState || currentState.loading)),
        error: meError || currentState?.error || null,
        canEdit,
        saving,
        refresh,
        saveBooking,
        deleteBooking,
        refreshWarning: warning?.context === activeContext ? warning.error : null,
    };
};

export default useBookedSubcontractors;
